'use client';

import { ExternalToast, toast as sonnerToast } from 'sonner';
import { Typography } from '../../ui';
import {
  toastIconVariants,
  toastVariants,
  ToastVariants,
} from '../../patterns/toast';
import { match } from 'ts-pattern';
import {
  IconCheckCircle,
  IconExclamationCircle,
  IconExclamationTriangle,
  IconInfoCircle,
  IconTimes,
} from '@maia/icons';
import { twMerge } from 'tailwind-merge';
import { Button } from 'react-aria-components';

type Props = {
  id: string | number;
  title: string;
  description?: string;
  config?: ExternalToast;
  intent?: Exclude<ToastVariants['variant'], 'default' | null | undefined>;
};

function Toast(props: Props) {
  const { id, title, description, intent = 'info' } = props;

  return (
    <div className={toastVariants({ variant: intent, size: 'small' })}>
      {match(intent)
        .with('success', () => (
          <IconCheckCircle
            className={twMerge(
              toastIconVariants({ variant: intent }),
              'size-20',
            )}
          />
        ))
        .with('warning', () => (
          <IconExclamationTriangle
            className={twMerge(
              toastIconVariants({ variant: intent }),
              'size-20',
            )}
          />
        ))
        .with('error', () => (
          <IconExclamationCircle
            className={twMerge(
              toastIconVariants({ variant: intent }),
              'size-20',
            )}
          />
        ))
        .with('info', () => (
          <IconInfoCircle
            className={twMerge(
              toastIconVariants({ variant: intent }),
              'size-20',
            )}
          />
        ))
        .exhaustive()}

      <div className="flex flex-1 items-center">
        <div className="w-full">
          <Typography
            as="p"
            variant="body-xxs"
            weight="semibold"
            className="text-grayscale600"
          >
            {title}
          </Typography>

          {description && (
            <Typography
              as="p"
              variant="body-xxs"
              weight="regular"
              className="text-grayscale500"
            >
              {description}
            </Typography>
          )}
        </div>
      </div>

      <Button className="p-4 h-fit" onPress={() => sonnerToast.dismiss(id)}>
        <IconTimes
          className={twMerge(toastIconVariants({ variant: intent }), 'size-16')}
        />
      </Button>
    </div>
  );
}

export function showToast(props: Omit<Props, 'id'>) {
  const { title, description, intent = 'info', config } = props;
  return sonnerToast.custom(
    (id) => (
      <Toast id={id} title={title} description={description} intent={intent} />
    ),
    { ...config },
  );
}
